/**
 * Customer Profile editor — Name (required), Gender (optional), DOB (optional).
 *
 * Also hosts the irreversible **Delete account** flow at the bottom:
 *   1. Tap delete → backend sends a fresh OTP to the same mobile
 *   2. User enters OTP → backend soft-deletes the user + zeros wallet + coins
 *   3. UI logs out and routes home
 */
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Trash2, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { BkPageHeader } from "../../components/blinkit/BkUI";

const GENDERS = [
    { value: "MALE", label: "Male" },
    { value: "FEMALE", label: "Female" },
    { value: "OTHER", label: "Other" },
    { value: "PREFER_NOT_TO_SAY", label: "Prefer not to say" },
];

export default function ProfileEditPage() {
    // Blinkit-theme tenants get the shared Blinkit header; the page body is the same for every theme.
    const blinkit = useIsBlinkit();
    const { user, logout, refresh } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({ first_name: "", gender: "", date_of_birth: "" });
    const [saving, setSaving] = useState(false);
    const [del, setDel] = useState(null); // { stage: 'confirm'|'otp', otp: '' }
    const [otp, setOtp] = useState("");
    const [delBusy, setDelBusy] = useState(false);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        (async () => {
            if (cancelled) return;
            setForm({
                first_name: user.first_name || "",
                gender: user.gender || "",
                date_of_birth: user.date_of_birth || "",
            });
        })();
        return () => { cancelled = true; };
    }, [user]);

    const save = async () => {
        if (!form.first_name.trim()) { toast.error("Name is required"); return; }
        setSaving(true);
        try {
            await api.patch("/auth/me/profile", {
                first_name: form.first_name.trim(),
                gender: form.gender || null,
                date_of_birth: form.date_of_birth || null,
            });
            toast.success("Profile saved");
            try { await refresh?.(); } catch (err) { console.debug("[ProfileEdit] refresh skipped:", err); }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const askDeleteOtp = async () => {
        setDelBusy(true);
        try {
            await api.post("/auth/otp/request", {
                mobile_number: user.mobile_number,
                role: user.role,
                channel: "SMS",
            });
            toast.success("OTP sent — enter it to confirm deletion");
            setDel({ stage: "otp" });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to send OTP");
        } finally { setDelBusy(false); }
    };

    const confirmDelete = async () => {
        if (otp.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
        setDelBusy(true);
        try {
            await api.post("/auth/me/delete", { otp });
            toast.success("Account deleted. Goodbye!");
            await logout();
            nav("/");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        } finally { setDelBusy(false); }
    };

    if (!user) return null;

    return (
        <div className="pb-32 max-w-xl mx-auto lg:max-w-none">
            {blinkit ? (
                <BkPageHeader title="Edit profile" back="/profile" right={
                    <button onClick={save} disabled={saving} data-testid="profile-save"
                            className="h-9 px-3.5 rounded-lg bg-primary text-primary-foreground text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                    </button>
                } />
            ) : (
                <header className="px-4 pt-5 pb-4 flex items-center gap-3 bg-card border-b border-border sticky top-0 z-10">
                    <Link to="/profile" className="p-1 -ml-1" data-testid="profile-edit-back"><ArrowLeft size={22} /></Link>
                    <h1 className="text-lg font-bold flex-1">Edit profile</h1>
                    <button onClick={save} disabled={saving} className="rounded-xl bg-primary text-primary-foreground px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="profile-save">
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                    </button>
                </header>
            )}

            {/* iter178 — desktop (lg+): two columns, same convention as Cart / Checkout. Below lg both wrappers are plain
                blocks, so phones and tablets render exactly as before. */}
            <div className="lg:grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:items-start">
            <div className="min-w-0">
            <section className="m-4 bg-card rounded-2xl border border-border p-5 space-y-4">
                <div>
                    <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Full Name <span className="text-red-500">*</span></label>
                    <input
                        data-testid="profile-name-input"
                        value={form.first_name}
                        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                        placeholder="e.g. Aarav Sharma"
                        className="mt-1 w-full bg-muted rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 ring-primary/30"
                    />
                </div>

                <div>
                    <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Gender <span className="text-muted-foreground/60">(optional)</span></label>
                    <div className="mt-2 grid grid-cols-2 gap-2" data-testid="profile-gender">
                        {GENDERS.map((g) => (
                            <button
                                key={g.value}
                                type="button"
                                onClick={() => setForm({ ...form, gender: form.gender === g.value ? "" : g.value })}
                                data-testid={`profile-gender-${g.value}`}
                                className={`px-3 py-2 text-xs font-semibold rounded-xl border transition ${form.gender === g.value ? "border-primary bg-primary/10 text-primary" : "border-border bg-muted/40 hover:bg-muted"}`}
                            >{g.label}</button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Date of Birth <span className="text-muted-foreground/60">(optional)</span></label>
                    <input
                        type="date"
                        data-testid="profile-dob"
                        value={form.date_of_birth}
                        onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                        max={new Date().toISOString().slice(0, 10)}
                        className="mt-1 w-full bg-muted rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 ring-primary/30"
                    />
                </div>
            </section>

            </div>
            <div className="min-w-0">
            <section className="m-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-2xl p-5" data-testid="profile-danger-zone">
                <p className="text-[11px] uppercase tracking-wider font-bold text-red-700 dark:text-red-300 inline-flex items-center gap-1.5"><AlertTriangle size={12} /> Danger zone</p>
                <h3 className="font-bold mt-1">Delete my account</h3>
                <p className="text-xs text-muted-foreground mt-1">This permanently removes your profile. Your <b>wallet balance and coins will be forfeited</b> — please withdraw or spend them first.</p>
                <button
                    onClick={() => setDel({ stage: "confirm" })}
                    data-testid="profile-delete-btn"
                    className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-xs font-bold"
                ><Trash2 size={13} /> Delete account</button>
            </section>
            </div>
            </div>

            {del && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !delBusy && setDel(null)}>
                    <div className="bg-card rounded-3xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="profile-delete-modal">
                        {del.stage === "confirm" ? (
                            <>
                                <h2 className="text-lg font-bold">Are you sure?</h2>
                                <p className="text-sm text-muted-foreground mt-2">
                                    We&apos;ll send a one-time code to <b>+91 {user.mobile_number}</b> to confirm.
                                    Once deleted, this cannot be undone. Wallet balance and coins will be forfeited.
                                </p>
                                <div className="flex gap-2 mt-5">
                                    <button onClick={() => setDel(null)} className="flex-1 rounded-xl bg-muted py-2.5 text-sm font-semibold" data-testid="profile-delete-cancel">Cancel</button>
                                    <button onClick={askDeleteOtp} disabled={delBusy} className="flex-1 rounded-xl bg-red-600 text-white py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60" data-testid="profile-delete-send-otp">
                                        {delBusy && <Loader2 size={13} className="animate-spin" />} Send OTP
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h2 className="text-lg font-bold">Confirm deletion</h2>
                                <p className="text-sm text-muted-foreground mt-1">Enter the 6-digit code we sent to <b>+91 {user.mobile_number}</b>.</p>
                                <input
                                    data-testid="profile-delete-otp"
                                    inputMode="numeric"
                                    maxLength={6}
                                    placeholder="6-digit code"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                    className="mt-4 w-full bg-muted rounded-xl px-4 py-3 text-lg tracking-[0.4em] text-center font-bold outline-none"
                                />
                                <div className="flex gap-2 mt-5">
                                    <button onClick={() => setDel(null)} className="flex-1 rounded-xl bg-muted py-2.5 text-sm font-semibold" data-testid="profile-delete-cancel-otp">Cancel</button>
                                    <button onClick={confirmDelete} disabled={delBusy} className="flex-1 rounded-xl bg-red-600 text-white py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60" data-testid="profile-delete-confirm">
                                        {delBusy && <Loader2 size={13} className="animate-spin" />} Delete forever
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
